import {
  BettingInterest,
  Handicapping,
  RunnerFlags,
  Timeform
} from "@tvg/ts-types/Race";
import { mathPercentage } from "@tvg/utils/mathUtils";

export interface RunnerBIComputed {
  currentOddNumeric?: number;
  top3WinPercentage?: number;
  jockeyTrainerWinPercentage?: number;
  jockeyTrainerTop3Percentage?: number;
}

export interface RunnerBI extends RunnerBIComputed {
  biNumber: number;
  runnerName: string;
  isScratched: boolean;
  isFavorite?: boolean;
  number: string;
  numberColor: string;
  saddleColor: string;
  jockey: string;
  trainer: string;
  currentOdd: string;
  morningLineOdd: string;
  timeform?: Timeform;
  handicapping?: Handicapping;
  runnerDob: number | undefined;
  entityRunnerId: string | undefined;
  silkImage?: string;
  tags?: RunnerFlags;
  age?: number;
  weight?: number;
  med?: string;
  sex?: string;
  sire?: string;
  dam?: string;
  grandDam?: string;
  owner?: string;
  numberOfFirstPlace?: number;
  numberOfSecondPlace?: number;
  numberOfThirdPlace?: number;
  numberOfStarts?: number;
  winPercentage?: number;
  top3Percentage?: number;
  powerRating?: number;
  daysOff?: number;
  jockeyTrainerStarts?: number;
  jockeyTrainerWins?: number;
  jockeyTrainerPlaces?: number;
  jockeyTrainerShows?: number;
  avgSpeed?: number;
  highSpeed?: number;
  avgClass?: number;
  averagePaceEarly?: number;
  averagePaceMiddle?: number;
  averagePaceFinish?: number;
}

export const computeWinPercentage = (
  starts: number | undefined,
  ...wins: Array<number | undefined>
) => {
  if (
    typeof starts !== "number" ||
    !wins?.length ||
    wins.some((win) => typeof win !== "number")
  ) {
    return undefined;
  }

  const winSum = (wins as Array<number>).reduce((sum, win) => sum + win, 0);
  return winSum <= starts ? mathPercentage(starts, winSum) : undefined;
};

const useGetRunnerFromBI = (
  bettingInterests: BettingInterest[],
  options: { withComputedProps: boolean } = {
    withComputedProps: false
  }
) => {
  if (!bettingInterests?.length) {
    return [];
  }
  const runners: RunnerBI[] = bettingInterests.flatMap(
    (bettingInterest: BettingInterest) =>
      bettingInterest.runners?.map((runner) => {
        const runnerBI: RunnerBI = {
          biNumber: bettingInterest.biNumber,
          runnerName: runner?.horseName,
          isScratched: runner.scratched,
          isFavorite: bettingInterest?.favorite,
          number: runner?.runnerId,
          numberColor: bettingInterest.numberColor,
          saddleColor: bettingInterest.saddleColor,
          jockey: runner?.jockey,
          trainer: runner?.trainer,
          currentOdd: bettingInterest?.currentOdds
            ? `${bettingInterest?.currentOdds?.numerator}${
                bettingInterest?.currentOdds?.denominator
                  ? `/${bettingInterest?.currentOdds?.denominator}`
                  : ""
              }`
            : "99",
          morningLineOdd: bettingInterest?.morningLineOdds
            ? `${bettingInterest?.morningLineOdds?.numerator}${
                bettingInterest?.morningLineOdds?.denominator
                  ? `/${bettingInterest?.morningLineOdds?.denominator}`
                  : ""
              }`
            : "99",
          silkImage: runner?.timeform?.silkUrlSvg,
          tags: runner?.timeform?.flags,
          entityRunnerId: runner?.entityRunnerId || "",
          timeform: runner?.timeform,
          handicapping: runner?.handicapping,
          runnerDob: runner.dob,
          age: runner?.age,
          weight: runner?.weight,
          med: runner?.med,
          sex: runner?.sex,
          sire: runner?.sire,
          dam: runner?.dam,
          grandDam: runner?.damSire,
          owner: runner?.ownerName,
          numberOfFirstPlace:
            runner?.handicapping?.pastResults?.numberOfFirstPlace,
          numberOfSecondPlace:
            runner?.handicapping?.pastResults?.numberOfSecondPlace,
          numberOfThirdPlace:
            runner?.handicapping?.pastResults?.numberOfThirdPlace,
          numberOfStarts:
            runner?.handicapping?.pastResults?.totalNumberOfStarts,
          winPercentage: runner?.handicapping?.pastResults?.winPercentage,
          top3Percentage: runner?.handicapping?.pastResults?.top3Percentage,
          powerRating: runner?.handicapping?.snapshot.powerRating,
          daysOff: runner?.handicapping?.snapshot.daysOff,
          jockeyTrainerStarts: runner?.handicapping?.jockeyTrainer.starts,
          jockeyTrainerWins: runner?.handicapping?.jockeyTrainer.wins,
          jockeyTrainerPlaces: runner?.handicapping?.jockeyTrainer.places,
          jockeyTrainerShows: runner?.handicapping?.jockeyTrainer.shows,
          avgSpeed: runner?.handicapping?.speedAndClass.avgSpeed,
          highSpeed: runner?.handicapping?.speedAndClass.highSpeed,
          avgClass: runner?.handicapping?.speedAndClass.avgClassRating,
          averagePaceEarly: runner?.handicapping?.averagePace.early,
          averagePaceMiddle: runner?.handicapping?.averagePace.middle,
          averagePaceFinish: runner?.handicapping?.averagePace.finish
        };

        if (options.withComputedProps) {
          runnerBI.top3WinPercentage = computeWinPercentage(
            runnerBI.numberOfStarts,
            runnerBI.numberOfFirstPlace,
            runnerBI.numberOfSecondPlace,
            runnerBI.numberOfThirdPlace
          );

          runnerBI.jockeyTrainerWinPercentage = computeWinPercentage(
            runnerBI.jockeyTrainerStarts,
            runnerBI.jockeyTrainerWins
          );

          runnerBI.jockeyTrainerTop3Percentage = computeWinPercentage(
            runnerBI.jockeyTrainerStarts,
            runnerBI.jockeyTrainerWins,
            runnerBI.jockeyTrainerPlaces,
            runnerBI.jockeyTrainerShows
          );

          const [numerator, denominator] = runnerBI.currentOdd.split("/");
          runnerBI.currentOddNumeric = (+numerator || 0) / (+denominator || 1);
        }

        return runnerBI;
      })
  );

  return runners;
};

export default useGetRunnerFromBI;
